import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/brand/brand/apps/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import ComponentLayout from '../../../src/layouts/component-layout';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const layoutProps = {
  _frontmatter
};
const MDXLayout = ComponentLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <h2>{`Anatomy`}</h2>
    <p><img parentName="p" {...{
        "src": "https://github.com/primer/brand/assets/6951037/d018c27f-847d-4011-b920-d15acb1e5efc",
        "alt": "An image displaying a the CTA form anatomy, with a label on top of an input form, followed by a CTA button and optional checkbox underneath the input."
      }}></img></p>
    <ul>
      <li parentName="ul">{`Label: Short text that describes the required input form.`}</li>
      <li parentName="ul">{`Text input: Single-line text field`}</li>
      <li parentName="ul">{`Action: Call-to-action button that corresponds after completing the input form.`}</li>
      <li parentName="ul">{`Checkbox: An optional checkbox to request additional behavior.`}</li>
    </ul>
    <h2>{`Usage`}</h2>
    <p>{`The CTA form is designed to capture user input and call to actions on a website. It serves as an efficient tool to prompt user engagement and action. While it can be placed throughout the page, it is encouraged to be positioned at the bottom to conclude the user journey, summarize key information, and encourage users to take action.`}</p>
    <p>{`The CTA form presents a concise label, prompting users to provide specific input in the form field. Once users enter the required information, they can trigger the call-to-action through the button. Additionally, the CTA form supports an optional checkbox to seek additional user behavior or acknowledge certain terms.`}</p>
    <p>{`By adopting the same guidelines as the respective `}<a parentName="p" {...{
        "href": "/components/TextInput"
      }}>{`text input`}</a>{` component, `}<a parentName="p" {...{
        "href": "/components/Button"
      }}>{`button`}</a>{` component, and `}<a parentName="p" {...{
        "href": "/components/Checkbox"
      }}>{`checkbox`}</a>{` component, the CTA form ensures consistency in design and enhances user familiarity with the interface.`}</p>
    <p>{`We recommend the CTA form over the `}<a parentName="p" {...{
        "href": "/components/CTABanner"
      }}>{`CTA banner`}</a>{` when the main action for a user to take is to submit a form (e.g subscribe to a newsletter) vs taking actions (sign in or bring the user to a pricing page).`}</p>
    <DoDontContainer stacked mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://github.com/primer/brand/assets/6951037/220b211e-f61f-4afb-83de-6a58980cdd6f" />
    <Caption mdxType="Caption">
      Place the CTA form near the end of the page to conclude the user journey
      and encourage users to take action.
    </Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://github.com/primer/brand/assets/6951037/3f202055-75ae-44be-82ca-f5388d0d3a1c" />
    <Caption mdxType="Caption">
      Don't place the CTA Form early on in the user journey. Use the Hero
      instead for early on.
    </Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Setting Context`}</h3>
    <p>{`Keep the CTA Form simple and intuitive to minimize friction and encourage user engagement. Ensure there is enough context surrounding the component. Utilize the label as a description of the form input.`}</p>
    <DoDontContainer stacked mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://github.com/primer/brand/assets/6951037/04aeb96e-2c24-413e-94be-3608a0e41ec7" />
    <Caption mdxType="Caption">
      Include context in areas surrounding the CTA Form to encourage user
      engagement.
    </Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://github.com/primer/brand/assets/6951037/f0daf653-30ef-42d1-afac-354959c112c7" />
    <Caption mdxType="Caption">
      Don't use the CTA Form without context. The label should be used to
      describe the input, not to provide context.
    </Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Component length`}</h3>
    <p>{`When implementing CTA form, it's important to consider the length of the input field in order to maintain consistency and enhance user familiarity with the interface. Keeping the string length (width) of the component relative to the expected input is a recommended practice. Pair the component with additional information or context instead of spanning in wide viewports.`}</p>
    <DoDontContainer stacked mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://github.com/primer/brand/assets/6951037/ba970b9c-cef8-4cf3-987f-15a11d6e27d2" />
    <Caption mdxType="Caption">
      Keep the string length (width) of the component relative to the expected
      input.
    </Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://github.com/primer/brand/assets/6951037/199c7caa-e605-4f53-9f41-e3d2460f7c55" />
    <Caption mdxType="Caption">Don't over extend or compress the CTA form when possible.</Caption>
  </Dont>
    </DoDontContainer>
    <h2>{`Options`}</h2>
    <h3>{`Checkbox`}</h3>
    <p>{`Use the checkbox property in the CTA form to prompt additional user behavior or seek acknowledgment– examples include to gather consent, confirm agreement to terms, or request optional actions.`}</p>
    <p>{`It is crucial to provide a clear description of the checkbox's purpose and hyperlink to relevant resources. When using the checkbox, completion of the checkbox must be a requirement (not optional) to proceed with the CTA.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://github.com/primer/brand/assets/6951037/0db61c2b-ad25-4dee-9c6f-eea294315d22" />
    <Caption mdxType="Caption">
      Use short and concise text for the checkbox and inline links to relevant
      resources.
    </Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://github.com/primer/brand/assets/6951037/59f8566f-8679-46b3-8b10-148c9fec104a" />
    <Caption mdxType="Caption">Don't use lengthy text or unstlyed links.</Caption>
  </Dont>
    </DoDontContainer>
    <h2>{`Related components`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/Button"
        }}>{`Button`}</a>{`: To display a button that preforms an action.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/TextInput"
        }}>{`Text input`}</a>{`: To create an input form for single-line text field.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/Checkbox"
        }}>{`Checkbox`}</a>{`: To select options or acknowledge information.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/CTABanner"
        }}>{`CTA Banner`}</a>{`: To draw attention to or create urgency around a user action.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      